/**
 * API service for ad image generation (SSE-streamed)
 */

import { invokeSSE } from "./sseClient";
import type { ProductAnalysis } from "@shared/schemas/product";
import type { GenerateAdRequest } from "@shared/schemas/api";
import type { SSEStepEvent, SSEImageEvent, SSEDoneEvent } from "@shared/schemas/sse";

export type { ProductAnalysis } from "@shared/schemas/product";
export type { GenerateAdRequest } from "@shared/schemas/api";

/** Frontend-only params (includes imageCount not in API schema) */
export interface GenerateAdParams extends GenerateAdRequest {
  imageCount?: number;
}

export interface GenerateAdCallbacks {
  onImage: (imageUrl: string, imageType: string) => void;
  onStep?: (step: number, type: string) => void;
  onDone: (images: string[], analysis?: ProductAnalysis) => void;
  onError: (error: string) => void;
}

/**
 * Call the generate-ad-images API via SSE stream.
 * Images arrive one-by-one through the onImage callback.
 */
export async function generateAdImages(
  params: GenerateAdParams,
  callbacks: GenerateAdCallbacks
): Promise<void> {
  await invokeSSE("/api/generate-ad-images", params, {
    onEvent: (event, data) => {
      if (event === "step" && callbacks.onStep) {
        const d = data as SSEStepEvent;
        callbacks.onStep(d.step, d.type);
      } else if (event === "image") {
        const d = data as SSEImageEvent;
        callbacks.onImage(d.url, d.type);
      } else if (event === "done") {
        const d = data as SSEDoneEvent;
        callbacks.onDone(d.images, d.analysis);
      }
    },
    onError: (error) => callbacks.onError(error),
  });
}
